// src/constants/currencies.ts
var CURRENCIES = ["EUR", "USD", "GBP"];

// src/constants/delivery.ts
var DEFAULT_DELIVERY_TIMESLOT_DURATION_IN_HOURS = 2;
var MIN_DELIVERY_TIMESLOT_DURATION_IN_HOURS = 1;
var MAX_DELIVERY_TIMESLOT_DURATION_IN_HOURS = 12;
var DEFAULT_DURATION_TIME_MINUTES = 5;

// src/constants/languages.ts
var SUPPORTED_LANGUAGES = ["en", "fr", "ar"];
var RTL_LANGUAGES = ["ar", "he", "fa"];

// src/constants/password.ts
var PASSWORD_MIN_LENGTH = 8;
var PASSWORD_MAX_LENGTH = 16;
var PASSWORD_REGEX = new RegExp(
  `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^a-zA-Z\\d]).{${PASSWORD_MIN_LENGTH},${PASSWORD_MAX_LENGTH}}$`
);
var PASSWORD_LOWERCASE_REGEX = /[a-z]/;
var PASSWORD_UPPERCASE_REGEX = /[A-Z]/;
var PASSWORD_NUMBER_REGEX = /\d/;
var PASSWORD_SYMBOL_REGEX = /[^a-zA-Z\d]/;

// src/constants/phone.ts
var PHONE_NATIONAL_NUMBER_REGEX = /^\d{4,15}$/;
var DEFAULT_DIAL_CODE = "+33";

// src/jwt.ts
var JWT_KIND = {
  INTERNAL: "internal",
  SERVICE: "service",
  SIGNUP: "signup",
  USER: "user"
};
var MAX_REFRESH_SESSION_MS = 30 * 24 * 60 * 60 * 1e3;

// src/test-factories/object.factory.ts
var ObjectFactory = class {
  /**
   * Build a new instance of T with optional overrides.
   * Overrides are spread on top of defaults, so you only specify what differs.
   */
  build(overrides = {}) {
    return {
      ...this.getDefaults(),
      ...overrides
    };
  }
  /**
   * Create a modified copy of an existing object with partial overrides.
   * Useful for testing mutations, updates, or "before/after" scenarios.
   */
  modify(base, overrides) {
    return {
      ...base,
      ...overrides
    };
  }
};

// src/test-factories/base-document.factory.ts
var counter = 0;
var BaseDocumentFactory = class extends ObjectFactory {
  getDefaults() {
    counter += 1;
    return {
      createdAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      deletedAt: null,
      id: `test-id-${counter}`,
      tenantId: "aaaa00001111bbbb2222cccc",
      updatedAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z")
    };
  }
};
var baseDocumentFactory = new BaseDocumentFactory();

// src/billing/billing.interface.ts
var DEFAULT_FREE_TRIAL_DURATION_IN_DAYS = 30;
var TENANT_BILLING_CONFIGURATION_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending",
  UPCOMING: "upcoming"
};
var BILL_LINE_KEYS = {
  FREE_TRIAL_ADJUSTMENT: "free-trial-adjustment",
  MONTHLY_FEE: "monthly-fee",
  PRICE_CAP_ADJUSTMENT: "price-cap-adjustment",
  TASKS: "tasks",
  VEHICLES: "vehicles"
};
var BILLING_CONSTRAINTS = { VEHICLE_MAX_COUNT: "vehicleMaxCount" };

// src/test-factories/bill.factory.ts
var BillFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      billingPeriod: { month: 9, year: 2026 },
      computedAt: /* @__PURE__ */ new Date("2026-10-01T00:00:00.000Z"),
      currency: "EUR",
      lines: [{ amount: 99, key: BILL_LINE_KEYS.MONTHLY_FEE }],
      locked: false,
      metrics: { activeVehicleCount: 0, taskCount: 0, vehicleActivityDays: 0 },
      planName: "standard",
      total: 99,
      violations: []
    };
  }
};
var billFactory = new BillFactory();

// src/test-factories/billing-counted-task.factory.ts
var BillingCountedTaskFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      completedAt: /* @__PURE__ */ new Date("2026-09-15T08:00:00.000Z"),
      countedAt: /* @__PURE__ */ new Date("2026-09-15T10:00:00.000Z"),
      month: 9,
      taskId: "aabbccddeeff001122334455",
      year: 2026
    };
  }
  buildCreate(overrides = {}) {
    const { completedAt, countedAt, month, taskId, year } = this.build(overrides);
    return { completedAt, countedAt, month, taskId, year };
  }
};
var billingCountedTaskFactory = new BillingCountedTaskFactory();

// src/test-factories/billing-metrics.factory.ts
var BillingMetricsFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      month: 9,
      taskCount: 0,
      year: 2026
    };
  }
};
var billingMetricsFactory = new BillingMetricsFactory();

// src/test-factories/billing-plan.factory.ts
var BillingPlanFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      configuration: {
        constraints: { vehicleMaxCount: 20 },
        fees: { monthlyFee: 99, monthlyVehicleFee: 5, taskFee: 0.5 },
        limits: { monthlyPriceCap: 5e3 }
      },
      currency: "EUR",
      name: "standard"
    };
  }
  buildCreate(overrides = {}) {
    const { configuration, currency, name } = this.build(overrides);
    return { configuration, currency, name };
  }
  /** Immutable creation-time snapshot shape embedded into a `TenantBillingConfiguration`. */
  buildSummary(overrides = {}) {
    const { configuration, currency, id, name } = this.build(overrides);
    return { configuration, currency, id, name };
  }
  /** Adds the computed `inUse` flag (defaults to `false`) on top of `build()`. */
  buildView(overrides = {}) {
    const { inUse = false, ...rest } = overrides;
    return { ...this.build(rest), inUse };
  }
};
var billingPlanFactory = new BillingPlanFactory();

// src/test-factories/billing-settings.factory.ts
var BillingSettingsFactory = class extends ObjectFactory {
  getDefaults() {
    return { ...baseDocumentFactory.build(), freeTrialDurationInDays: DEFAULT_FREE_TRIAL_DURATION_IN_DAYS };
  }
  buildCreate(overrides = {}) {
    const { freeTrialDurationInDays } = this.build(overrides);
    return { freeTrialDurationInDays };
  }
};
var billingSettingsFactory = new BillingSettingsFactory();

// src/test-factories/tenant-billing-configuration.factory.ts
var DEFAULT_PLAN_ID = "bbbbbbbbbbbbbbbbbbbbbbbb";
var TenantBillingConfigurationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      from: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      overrides: {},
      plan: billingPlanFactory.buildSummary({ id: DEFAULT_PLAN_ID, name: "standard" }),
      status: TENANT_BILLING_CONFIGURATION_STATUS.ACTIVE,
      to: /* @__PURE__ */ new Date("2026-12-31T23:59:59.999Z"),
      trialStartedAt: null
    };
  }
  /** `plan` is resolved server-side from `planId` at creation time — never client-supplied. */
  buildCreate(overrides = {}) {
    const { planId: planIdOverride, ...buildOverrides } = overrides;
    const { from, overrides: configOverrides, plan, to, trialStartedAt } = this.build(buildOverrides);
    return { from, overrides: configOverrides, planId: planIdOverride ?? plan.id, to, trialStartedAt };
  }
};
var tenantBillingConfigurationFactory = new TenantBillingConfigurationFactory();

// src/outgoing-webhooks/call-request.interface.ts
var CALL_STATUS = {
  ABANDONED: "abandoned",
  CANCELLED: "cancelled",
  DONE: "done",
  IN_PROGRESS: "inprogress",
  TODO: "todo",
  TO_RETRY: "to-retry"
};

// src/test-factories/call-request.factory.ts
var CallRequestFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      calls: [],
      correlationId: "correlation-id",
      incomingEventId: "incoming-event-id",
      nextCallDate: null,
      processingTimeout: null,
      status: CALL_STATUS.TODO,
      webhookConfigId: "webhook-config-id"
    };
  }
  /** Build a CreateCallRequest body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      calls: [],
      correlationId: "correlation-id",
      incomingEventId: "incoming-event-id",
      nextCallDate: null,
      processingTimeout: null,
      status: CALL_STATUS.TODO,
      webhookConfigId: "webhook-config-id",
      ...overrides
    };
  }
};
var callRequestFactory = new CallRequestFactory();

// src/test-factories/endpoint-config.factory.ts
var EndpointConfigFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      active: true,
      identifiers: { shipperCode: "default-shipper" },
      method: "POST",
      name: "default-endpoint",
      urlTemplate: "https://api.example.com/v2/webhook"
    };
  }
  /** Build a CreateEndpointConfig body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      active: true,
      identifiers: { shipperCode: "default-shipper" },
      method: "POST",
      name: "default-endpoint",
      urlTemplate: "https://api.example.com/v2/webhook",
      ...overrides
    };
  }
};
var endpointConfigFactory = new EndpointConfigFactory();

// src/eta/eta.interface.ts
var ETA_FRESHNESS = {
  APPROXIMATE: "approximate",
  LIVE: "live",
  UNAVAILABLE: "unavailable"
};
var ETA_ANCHOR_KIND = {
  ARRIVED_AT_STOP: "arrived_at_stop",
  COMPLETED_JOB: "completed_job",
  LAST_POSITION: "last_position"
};

// src/vehicle-template/vehicle-template.interface.ts
var VEHICLE_PROFILES = {
  BICYCLE: "bicycle",
  LARGE_VEHICLE: "large-vehicle",
  LIGHTWEIGHT_VEHICLE: "lightweight-vehicle",
  PEDESTRIAN: "pedestrian"
};

// src/test-factories/eta.factory.ts
var EtaFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      anchor: {
        departureTime: /* @__PURE__ */ new Date("2026-05-22T08:00:00"),
        kind: ETA_ANCHOR_KIND.LAST_POSITION,
        position: { latitude: 0, longitude: 0 }
      },
      computedAt: /* @__PURE__ */ new Date("2026-05-22T09:00:00"),
      requestedAt: /* @__PURE__ */ new Date("2026-05-22T09:00:00"),
      state: ETA_FRESHNESS.APPROXIMATE,
      value: /* @__PURE__ */ new Date("2026-05-22T15:30:00"),
      vehicleProfile: { profile: VEHICLE_PROFILES.LIGHTWEIGHT_VEHICLE }
    };
  }
};
var etaFactory = new EtaFactory();

// src/customer/customer.interface.ts
var CUSTOMER_TYPE = {
  CLIENT: "client",
  COMPANY: "company"
};
var CUSTOMER_STATUS = {
  VALIDATED: "validated"
};

// src/geolocation.ts
var GEOCODING_SOURCE = {
  AUTOCOMPLETE: "autocomplete",
  CLIENT: "client",
  FRONT_OFFICE: "front_office",
  KLAREO: "klareo",
  MANUAL: "manual"
};

// src/test-factories/customer.factory.ts
var CustomerFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      addresses: [
        {
          location: {
            address: {
              city: "Paris",
              country: "FR",
              postalCode: "75001",
              street: "1 Rue de la Paix"
            },
            coordinates: {
              latitude: 48.8566,
              longitude: 2.3522,
              score: 0.9,
              source: GEOCODING_SOURCE.KLAREO
            }
          },
          originalAddress: "1 Rue de la Paix, 75001 Paris, FR"
        }
      ],
      firstName: "John",
      lastName: "Doe",
      phone: { dialCode: "+33", number: "612345678" },
      siteIds: ["aabbccddeeff001122334455"],
      spaceIds: ["bbbbbbbbbbbbbbbbbbbbbbbb"],
      status: CUSTOMER_STATUS.VALIDATED,
      type: CUSTOMER_TYPE.CLIENT
    };
  }
  /** Build a CreateCustomer body (no auto-generated fields). */
  buildCreate(overrides = {}) {
    return {
      addresses: [
        {
          location: {
            address: {
              city: "Paris",
              country: "FR",
              postalCode: "75001",
              street: "1 Rue de la Paix"
            },
            coordinates: {
              latitude: 48.8566,
              longitude: 2.3522,
              score: 0.9,
              source: GEOCODING_SOURCE.KLAREO
            }
          },
          originalAddress: "1 Rue de la Paix, 75001 Paris, FR"
        }
      ],
      firstName: "John",
      lastName: "Doe",
      phone: { dialCode: "+33", number: "612345678" },
      siteIds: ["aabbccddeeff001122334455"],
      type: CUSTOMER_TYPE.CLIENT,
      ...overrides
    };
  }
};
var customerFactory = new CustomerFactory();

// src/test-factories/customer-address.factory.ts
var CustomerAddressFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      location: {
        address: { city: "Paris", country: "FR", postalCode: "75001", street: "1 Rue de la Paix" },
        isDefault: true
      },
      originalAddress: "1 Rue de la Paix, 75001 Paris, FR"
    };
  }
};
var customerAddressFactory = new CustomerAddressFactory();

// src/test-factories/carrier.factory.ts
var CarrierFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "DHL",
      enabledScopes: [],
      integration: false,
      name: "DHL Express"
    };
  }
};
var carrierFactory = new CarrierFactory();

// src/test-factories/carrier-summary.factory.ts
var CarrierSummaryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      code: "DHL",
      id: "eeeeeeeeeeeeeeeeeeeeeeee",
      name: "DHL Express"
    };
  }
};
var carrierSummaryFactory = new CarrierSummaryFactory();

// src/test-factories/eta-computed-payload.factory.ts
var EtaComputedPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      driverId: "aabbccddeeff001122334455",
      queueId: "bbccddeeff001122334455aa"
    };
  }
};
var etaComputedPayloadFactory = new EtaComputedPayloadFactory();

// src/test-factories/handling-unit.factory.ts
var HandlingUnitFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      id: "ffaabbccddeeff0011223344",
      quantity: 3,
      type: "PAL"
    };
  }
};
var handlingUnitFactory = new HandlingUnitFactory();

// src/test-factories/handling-unit-type.factory.ts
var HandlingUnitTypeFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "SML-PARCEL",
      label: "Small Parcel",
      volume: { unit: "L", value: 5 },
      weight: { unit: "kg", value: 2 }
    };
  }
};
var handlingUnitTypeFactory = new HandlingUnitTypeFactory();

// src/import/import.interface.ts
var IMPORT_KIND = {
  HANDLING_UNITS: "handling-units",
  ORDERS: "orders"
};
var IMPORT_STATE = {
  ERROR: "error",
  PARTIAL: "partial",
  PENDING: "pending",
  SUCCESS: "success"
};
var IMPORT_LINE_ERROR_CODE = {
  CANCELLED_ORDER: "cancelled_order",
  CREATE_ERROR: "create_error",
  EMIT_ERROR: "emit_error",
  /** Another line of the same order was invalid, so the whole order group was rejected (HU import replaces the full list, never a partial one). */
  ORDER_GROUP_ERROR: "order_group_error",
  ORDER_NOT_FOUND: "order_not_found",
  PARSE_ERROR: "parse_error",
  SCOPE_ERROR: "scope_error",
  UPDATE_ERROR: "update_error",
  VALIDATION_ERROR: "validation_error"
};

// src/test-factories/import-completed-payload.factory.ts
var ImportCompletedPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      fileName: "orders.csv",
      importId: "aabbccddeeff001122334455",
      kind: IMPORT_KIND.ORDERS,
      linesCount: {
        createError: 0,
        created: 3,
        parseError: 0,
        read: 3,
        skipped: 0,
        submitted: 3,
        total: 3,
        updateError: 0,
        updated: 0
      },
      state: IMPORT_STATE.SUCCESS,
      userId: "aabbccddeeff001122334456"
    };
  }
};
var importCompletedPayloadFactory = new ImportCompletedPayloadFactory();

// src/test-factories/import.factory.ts
var ImportFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      errors: { global: [], lines: [] },
      fileName: "orders.csv",
      filePath: "imports/orders/2026/09/09/uuid.csv",
      kind: IMPORT_KIND.ORDERS,
      linesCount: {
        createError: 0,
        created: 0,
        parseError: 0,
        read: 0,
        skipped: 0,
        submitted: 0,
        total: 0,
        updateError: 0,
        updated: 0
      },
      siteIds: [],
      spaceIds: [],
      state: IMPORT_STATE.PENDING,
      user: { firstName: "Alice", id: "aabbccddeeff001122334455", lastName: "Doe" }
    };
  }
};
var importFactory = new ImportFactory();

// src/test-factories/incoming-event.factory.ts
var IncomingEventFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      correlationId: "correlation-id",
      event: "on-site-created",
      identifiers: { tenantId: "aaaa00001111bbbb2222cccc" },
      payload: {}
    };
  }
  /** Build a CreateIncomingEvent body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      correlationId: "correlation-id",
      event: "on-site-created",
      identifiers: { tenantId: "aaaa00001111bbbb2222cccc" },
      payload: {},
      ...overrides
    };
  }
};
var incomingEventFactory = new IncomingEventFactory();

// src/kpi/kpi.interface.ts
var KPI_GRANULARITY = { DAY: "day", MONTH: "month", MONTH_TO_DATE: "mtd" };
var KPI_ACTIVE_HEARTBEAT_MS = 6e4;
var KPI_KEYS = { DRIVER_TASK_KPIS: "driver-task-kpis", ORDER_KPIS: "order-kpis" };
function isKpiKey(value) {
  return Object.values(KPI_KEYS).includes(value);
}
function isDriverTaskKpiRows(metrics) {
  return Array.isArray(metrics);
}

// src/test-factories/kpi.factory.ts
var KpiFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      from: { day: 1, month: 9, year: 2026 },
      granularity: KPI_GRANULARITY.MONTH,
      key: KPI_KEYS.ORDER_KPIS,
      lastUsed: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      to: { day: 30, month: 9, year: 2026 }
    };
  }
};
var kpiFactory = new KpiFactory();

// src/test-factories/kpi-computed-payload.factory.ts
var KpiComputedPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      from: { day: 1, month: 9, year: 2026 },
      granularity: KPI_GRANULARITY.MONTH,
      key: KPI_KEYS.ORDER_KPIS,
      kpiId: "aabbccddeeff001122334455",
      metrics: { anomaly: 0, completed: 3, failed: 0, inprogress: 1, total: 5, unassigned: 1 },
      to: { day: 30, month: 9, year: 2026 }
    };
  }
};
var kpiComputedPayloadFactory = new KpiComputedPayloadFactory();

// src/servicebus/kpi.ts
var KPI_COMPUTE_TRIGGER = { API: "api", CRON: "cron" };

// src/test-factories/kpi-compute-request-payload.factory.ts
var KpiComputeRequestPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      from: { day: 1, month: 9, year: 2026 },
      granularity: KPI_GRANULARITY.MONTH,
      key: KPI_KEYS.ORDER_KPIS,
      to: { day: 30, month: 9, year: 2026 },
      trigger: KPI_COMPUTE_TRIGGER.API,
      userId: "aabbccddeeff001122334455"
    };
  }
};
var kpiComputeRequestPayloadFactory = new KpiComputeRequestPayloadFactory();

// src/test-factories/kpi-refresh-tick-payload.factory.ts
var KpiRefreshTickPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return { generation: 1 };
  }
};
var kpiRefreshTickPayloadFactory = new KpiRefreshTickPayloadFactory();

// src/test-factories/item.factory.ts
var ItemFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      label: "Widget",
      quantity: 2
    };
  }
};
var itemFactory = new ItemFactory();

// src/history/history.interface.ts
var HISTORY_CREATED_EVENT_CODE = "created";
var HISTORY_VALUE_KIND = {
  ACTOR_REF: "actorRef",
  ADDRESS: "address",
  AMOUNT: "amount",
  LIST_DIFF: "listDiff",
  MEASUREMENT: "measurement",
  SCALAR: "scalar",
  STATUS: "status",
  TAGS: "tags",
  TIMESLOT: "timeslot"
};

// src/order/order.interface.ts
var ORDER_INSTRUCTION_MAX_LENGTH = 500;
var TRANSPORT_TYPE = {
  DELIVERY: "delivery",
  PICKUP: "pickup"
};
var ORDER_SOURCE_TYPE = {
  IMPORT: "import"
};
var ORDER_STATUS = {
  ANOMALY: "anomaly",
  ARRIVED_AT_DELIVERY: "arrived_at_delivery",
  ARRIVED_AT_PICKUP: "arrived_at_pickup",
  CANCELLED: "cancelled",
  CARRIER_ACCEPTED: "carrier_accepted",
  CARRIER_ASSIGNED: "carrier_assigned",
  CARRIER_REJECTED: "carrier_rejected",
  COMPLETED: "completed",
  DRAFT: "draft",
  DRIVER_ACCEPTED: "driver_accepted",
  DRIVER_ASSIGNED: "driver_assigned",
  DRIVING_TO_DELIVERY: "driving_to_delivery",
  DRIVING_TO_PICKUP: "driving_to_pickup",
  FAILED: "failed",
  IN_TRANSIT: "in_transit",
  NEW: "new",
  PENDING: "pending",
  PICKUP_COMPLETED: "pickup_completed",
  WAITING_FOR_PICKUP: "waiting_for_pickup"
};
function isOrderStatus(value) {
  return Object.values(ORDER_STATUS).includes(value);
}
var ORDER_STATUS_REASON = {
  CANCELLED_BY_EXTERNAL_SYSTEM: "cancelled_by_external_system",
  CANCELLED_BY_USER: "cancelled_by_user",
  GEOCODING_FAILED: "geocoding_failed",
  INVALID_TIME_WINDOW: "invalid_time_window",
  METRICS_VALIDATION_FAILED: "metrics_validation_failed",
  VALIDATION_PIPELINE_ERROR: "validation_pipeline_error"
};
var ORDER_STATUS_BUCKET = {
  PENDING: [ORDER_STATUS.PENDING],
  TODO: [
    ORDER_STATUS.ANOMALY,
    ORDER_STATUS.DRAFT,
    ORDER_STATUS.NEW,
    ORDER_STATUS.CARRIER_ASSIGNED,
    ORDER_STATUS.DRIVER_ASSIGNED,
    ORDER_STATUS.CARRIER_ACCEPTED,
    ORDER_STATUS.DRIVER_ACCEPTED,
    ORDER_STATUS.CARRIER_REJECTED,
    ORDER_STATUS.DRIVING_TO_PICKUP
  ],
  IN_PROGRESS: [
    ORDER_STATUS.ARRIVED_AT_PICKUP,
    ORDER_STATUS.WAITING_FOR_PICKUP,
    ORDER_STATUS.PICKUP_COMPLETED,
    ORDER_STATUS.IN_TRANSIT,
    ORDER_STATUS.DRIVING_TO_DELIVERY,
    ORDER_STATUS.ARRIVED_AT_DELIVERY
  ],
  DONE: [ORDER_STATUS.COMPLETED, ORDER_STATUS.FAILED, ORDER_STATUS.CANCELLED]
};
var TERMINAL_ORDER_STATUSES = [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELLED];
function isOrderCancellable(status) {
  return !TERMINAL_ORDER_STATUSES.includes(status);
}
var ORDER_HISTORY_EVENT_CODE = {
  CREATED: HISTORY_CREATED_EVENT_CODE,
  CUSTOMER_FIRST_NAME: "customerFirstName",
  CUSTOMER_LAST_NAME: "customerLastName",
  DELIVERY_ADDRESS: "deliveryAddress",
  DELIVERY_TIMESLOT: "deliveryTimeslot",
  HANDLING_UNITS: "handlingUnits",
  ITEMS: "items",
  ORDER_VALUE: "orderValue",
  PICKUP_TIMESLOT: "pickupTimeslot",
  REQUESTED_TIME_WINDOW: "requestedTimeWindow",
  STATUS: "status",
  TAGS: "tags",
  TOTAL_QUANTITY: "totalQuantity",
  TOTAL_VOLUME: "totalVolume",
  TOTAL_WEIGHT: "totalWeight"
};
var BARCODE_TYPES = {
  EAN13: "EAN13",
  SSCC: "SSCC"
};
var ADDRESS_IDENTITY_KEYS = [
  "city",
  "country",
  "houseNumber",
  "postalCode",
  "street"
];
var HANDLING_UNIT_TEMPERATURES = {
  DRY: "dry",
  FRESH: "fresh",
  FROZEN: "frozen"
};
function isHandlingUnitTemperature(value) {
  return Object.values(HANDLING_UNIT_TEMPERATURES).includes(value);
}
var Order = {
  collection: "orders",
  name: "order"
};

// src/site/site.interface.ts
var SITE_TYPE = {
  RELAY: "RELAY",
  SHOP: "SHOP",
  WAREHOUSE: "WAREHOUSE"
};
var Site = {
  collection: "sites",
  name: "site"
};

// src/test-factories/order.factory.ts
var OrderFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      customer: {
        id: "aaaaaaaaaaaaaaaaaaaaaaaa",
        firstName: "John",
        lastName: "Doe",
        phone: { dialCode: "+33", number: "612345678" }
      },
      delivery: {
        site: { code: "SITE01", color: "#000", id: "site-1", name: "Warehouse", type: SITE_TYPE.WAREHOUSE }
      },
      orderType: { code: "STD", id: "ot-1", label: "Standard" },
      pickup: {
        durationTime: 15,
        site: { code: "PICKUP01", color: "#fff", id: "site-2", name: "Pickup Point", type: SITE_TYPE.SHOP }
      },
      referenceNumber: "REF-001",
      shipper: { id: "aaaaaaaaaaaaaaaaaaaaaaaa", code: "SHIPPER01", logo: "logo.png", name: "Shipper" },
      siteId: "site-1",
      spaceId: "space-1",
      status: ORDER_STATUS.NEW,
      transportType: TRANSPORT_TYPE.DELIVERY
    };
  }
  /** Build a CreateOrder body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      customer: { firstName: "John", lastName: "Doe", phone: { dialCode: "+33", number: "612345678" } },
      delivery: { durationTime: 30, siteCode: "DEL01" },
      orderTypeCode: "STD",
      pickup: { durationTime: 15, siteCode: "PICKUP01" },
      referenceNumber: "REF-001",
      shipperCode: "SHIPPER01",
      siteCode: "SITE01",
      transportType: TRANSPORT_TYPE.DELIVERY,
      ...overrides
    };
  }
};
var orderFactory = new OrderFactory();

// src/task/task.interface.ts
var TASK_TYPE = {
  DELIVERY: "delivery",
  NEW_ATTEMPT: "new_attempt",
  PICKUP: "pickup",
  RETURN: "return"
};
var TASK_RELATIONSHIP = {
  CHILD: "child",
  PARENT: "parent",
  SIBLING: "sibling"
};
var TASK_STATUS = {
  ARRIVED_AT_DELIVERY: "arrived_at_delivery",
  ARRIVED_AT_PICKUP: "arrived_at_pickup",
  CANCELLED: "cancelled",
  CARRIER_ACCEPTED: "carrier_accepted",
  CARRIER_ASSIGNED: "carrier_assigned",
  CARRIER_REJECTED: "carrier_rejected",
  COMPLETED: "completed",
  DRIVER_ACCEPTED: "driver_accepted",
  DRIVER_ASSIGNED: "driver_assigned",
  DRIVING_TO_DELIVERY: "driving_to_delivery",
  DRIVING_TO_PICKUP: "driving_to_pickup",
  FAILED: "failed",
  IN_TRANSIT: "in_transit",
  NEW: "new",
  PICKUP_COMPLETED: "pickup_completed",
  WAITING_FOR_PICKUP: "waiting_for_pickup"
};
var TASK_TERMINAL_STATUSES = [
  TASK_STATUS.CANCELLED,
  TASK_STATUS.COMPLETED,
  TASK_STATUS.FAILED
];
var TASK_HISTORY_PAGE_SIZE = 50;
var TASK_ASSIGNMENT_EDITABLE_STATUSES = [
  TASK_STATUS.NEW,
  TASK_STATUS.CARRIER_ASSIGNED,
  TASK_STATUS.CARRIER_ACCEPTED,
  TASK_STATUS.CARRIER_REJECTED,
  TASK_STATUS.DRIVER_ASSIGNED,
  TASK_STATUS.DRIVER_ACCEPTED,
  TASK_STATUS.DRIVING_TO_PICKUP
];
var TASK_STATUS_REASON = {
  ACCESS_IMPOSSIBLE: "access_impossible",
  ADDRESS_NOT_FOUND: "address_not_found",
  AUTO_ASSIGNED_REQUESTED_CARRIER: "auto_assigned_requested_carrier",
  CLOSED_PREMISES: "closed_premises",
  CUSTOMER_ABSENT: "customer_absent",
  DAMAGED_GOODS: "damaged_goods",
  GOODS_NOT_READY: "goods_not_ready",
  INCOMPLETE_ORDER: "incomplete_order",
  ORDER_CANCELLED: "order_cancelled",
  OTHER: "other",
  OUTDATED_BY_ORDER_UPDATE: "outdated_by_order_update",
  POSTPONED: "postponed",
  REFUSED_BY_CUSTOMER: "refused_by_customer",
  VEHICLE_BREAKDOWN: "vehicle_breakdown",
  WRONG_ADDRESS: "wrong_address"
};
var TASK_JOB_TYPE = {
  DELIVERY: "delivery",
  PICKUP: "pickup"
};
var QUEUE_PINNED_STATUSES_BY_JOB_TYPE = {
  [TASK_JOB_TYPE.DELIVERY]: /* @__PURE__ */ new Set([TASK_STATUS.ARRIVED_AT_DELIVERY]),
  [TASK_JOB_TYPE.PICKUP]: /* @__PURE__ */ new Set([TASK_STATUS.ARRIVED_AT_PICKUP, TASK_STATUS.WAITING_FOR_PICKUP])
};
function isQueueJobPinned(jobType, status) {
  return status !== TASK_STATUS.CANCELLED && QUEUE_PINNED_STATUSES_BY_JOB_TYPE[jobType].has(status);
}
var POSTPONABLE_STATUSES_BY_JOB_TYPE = {
  [TASK_JOB_TYPE.DELIVERY]: [TASK_STATUS.DRIVING_TO_DELIVERY, TASK_STATUS.ARRIVED_AT_DELIVERY],
  [TASK_JOB_TYPE.PICKUP]: [
    TASK_STATUS.DRIVING_TO_PICKUP,
    TASK_STATUS.ARRIVED_AT_PICKUP,
    TASK_STATUS.WAITING_FOR_PICKUP
  ]
};
var TASK_FAILURE_REASON = {
  ACCESS_IMPOSSIBLE: TASK_STATUS_REASON.ACCESS_IMPOSSIBLE,
  ADDRESS_NOT_FOUND: TASK_STATUS_REASON.ADDRESS_NOT_FOUND,
  CLOSED_PREMISES: TASK_STATUS_REASON.CLOSED_PREMISES,
  CUSTOMER_ABSENT: TASK_STATUS_REASON.CUSTOMER_ABSENT,
  DAMAGED_GOODS: TASK_STATUS_REASON.DAMAGED_GOODS,
  GOODS_NOT_READY: TASK_STATUS_REASON.GOODS_NOT_READY,
  INCOMPLETE_ORDER: TASK_STATUS_REASON.INCOMPLETE_ORDER,
  OTHER: TASK_STATUS_REASON.OTHER,
  REFUSED_BY_CUSTOMER: TASK_STATUS_REASON.REFUSED_BY_CUSTOMER,
  VEHICLE_BREAKDOWN: TASK_STATUS_REASON.VEHICLE_BREAKDOWN,
  WRONG_ADDRESS: TASK_STATUS_REASON.WRONG_ADDRESS
};
var BOTH_TASK_JOB_TYPES = [TASK_JOB_TYPE.DELIVERY, TASK_JOB_TYPE.PICKUP];
var TASK_FAILURE_REASON_JOB_TYPES = {
  [TASK_FAILURE_REASON.ACCESS_IMPOSSIBLE]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.ADDRESS_NOT_FOUND]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.CLOSED_PREMISES]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.CUSTOMER_ABSENT]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.DAMAGED_GOODS]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.GOODS_NOT_READY]: [TASK_JOB_TYPE.PICKUP],
  [TASK_FAILURE_REASON.INCOMPLETE_ORDER]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.OTHER]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.REFUSED_BY_CUSTOMER]: [TASK_JOB_TYPE.DELIVERY],
  [TASK_FAILURE_REASON.VEHICLE_BREAKDOWN]: BOTH_TASK_JOB_TYPES,
  [TASK_FAILURE_REASON.WRONG_ADDRESS]: BOTH_TASK_JOB_TYPES
};
var TASK_JOB_STATUS = {
  DONE: "done",
  FAILED: "failed",
  TO_DO: "to_do"
};
var TASK_PROOF_TYPE = {
  COMMENT: "comment",
  IMAGES: "images",
  SIGNATURE: "signature"
};
var PROOF_UPLOAD_EXTENSION = {
  JPG: "jpg",
  PNG: "png"
};
var PROOF_UPLOAD_MIME_TYPE = {
  [PROOF_UPLOAD_EXTENSION.JPG]: "image/jpeg",
  [PROOF_UPLOAD_EXTENSION.PNG]: "image/png"
};
var PROOF_BLOB_PATH_PREFIX = "proofs";
var MAX_STOP_PROOF_PHOTOS = 4;
var MAX_PROOF_UPLOAD_URLS_PER_CALL = 10;
var MAX_PROOF_BLOB_PATH_LENGTH = 128;
var PROOF_BLOB_PATH_REGEX = new RegExp(
  `^${PROOF_BLOB_PATH_PREFIX}/(?:\\d{4}/\\d{2}/\\d{2}/)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\\.(?:${Object.values(
    PROOF_UPLOAD_EXTENSION
  ).join("|")})$`
);
var HANDLING_UNIT_CONTROL_STATUS = {
  MARKED: "marked",
  // Expected at that leg but neither scanned nor marked at confirm.
  MISSING: "missing",
  SCANNED: "scanned"
};
var CONTROLLED_UNIT_SOURCE = {
  ADDITIONAL: "additional",
  EXPECTED: "expected"
};
var TASK_HISTORY_EVENT_CODE = {
  CARRIER: "carrier",
  CREATED: HISTORY_CREATED_EVENT_CODE,
  DRIVER: "driver",
  STATUS: "status"
};
var Task = {
  collection: "tasks",
  name: "task"
};

// src/test-factories/task.factory.ts
var DEFAULT_DELIVERY_JOB = {
  data: {
    site: {
      code: "DEL01",
      color: "#000000",
      id: "aabbccddeeff001122334455",
      name: "Delivery Site",
      type: SITE_TYPE.WAREHOUSE
    }
  },
  id: "aaaaaaaaaaaaaaaaaaaaaaaa",
  sortIndex: 1,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.DELIVERY
};
var DEFAULT_PICKUP_JOB = {
  data: {
    durationTime: 10,
    site: {
      code: "PICK01",
      color: "#000000",
      id: "bbccddeeff001122334455aa",
      name: "Pickup Site",
      type: SITE_TYPE.WAREHOUSE
    }
  },
  id: "bbbbbbbbbbbbbbbbbbbbbbbb",
  sortIndex: 0,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.PICKUP
};
var DeliveryJobFactory = class extends ObjectFactory {
  getDefaults() {
    return DEFAULT_DELIVERY_JOB;
  }
};
var deliveryJobFactory = new DeliveryJobFactory();
var PickupJobFactory = class extends ObjectFactory {
  getDefaults() {
    return DEFAULT_PICKUP_JOB;
  }
};
var pickupJobFactory = new PickupJobFactory();
var TaskFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      jobs: [DEFAULT_DELIVERY_JOB],
      order: {
        id: "bbccddeeff001122334455aa",
        referenceNumber: "REF-001",
        tags: [],
        totalHandlingUnitQuantity: void 0
      },
      site: {
        code: "SITE-01",
        color: "#FF5733",
        id: "ccddeeff001122334455aabb",
        name: "Main Site",
        type: SITE_TYPE.WAREHOUSE
      },
      spaceId: "ddeeff001122334455aabbcc",
      status: TASK_STATUS.ARRIVED_AT_DELIVERY,
      type: TASK_TYPE.DELIVERY,
      shipperId: "eeff001122334455aabbccdd"
    };
  }
  /** Builds a task with both a pickup job (sortIndex 0) and a delivery job (sortIndex 1). */
  buildWithBothJobs(overrides = {}) {
    return this.build({
      ...overrides,
      jobs: [DEFAULT_PICKUP_JOB, DEFAULT_DELIVERY_JOB]
    });
  }
};
var taskFactory = new TaskFactory();
function withProofs(job, proofs) {
  return { ...job, proofs };
}
function withUnitsControl(task, unitsControl) {
  return { ...task, unitsControl };
}

// src/test-factories/enriched-stop.factory.ts
var DEFAULT_DELIVERY_JOB2 = {
  data: {},
  id: "job-delivery-001",
  sortIndex: 0,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.DELIVERY
};
var DEFAULT_PICKUP_JOB2 = {
  data: {
    durationTime: 10,
    site: { code: "WH1", color: "#000", id: "site-1", name: "Warehouse", type: SITE_TYPE.WAREHOUSE }
  },
  id: "job-pickup-001",
  sortIndex: 0,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.PICKUP
};
function buildConsistentStopJob(taskOverrides = {}) {
  const order = orderFactory.build();
  const task = taskFactory.build({
    ...taskOverrides,
    order: {
      id: order.id,
      referenceNumber: order.referenceNumber,
      tags: order.tags,
      totalHandlingUnitQuantity: order.totalHandlingUnitQuantity
    }
  });
  return {
    order,
    task,
    taskJob: task.jobs[0]
  };
}
var EnrichedStopFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      fullAddress: "10 Rue de la Paix, 75001 Paris, France",
      id: "stop-001",
      jobs: [buildConsistentStopJob()],
      timeSlot: void 0
    };
  }
  /** Builds an enriched stop wrapping a delivery job; `dataOverrides` fully replaces the (empty) job data defaults. */
  buildDeliveryStop(dataOverrides = {}) {
    const taskJob = { ...DEFAULT_DELIVERY_JOB2, data: { ...dataOverrides } };
    const stopJob = buildConsistentStopJob({ jobs: [taskJob] });
    return this.build({ jobs: [stopJob] });
  }
  /** Builds an enriched stop wrapping a pickup job; `dataOverrides` merges onto the pickup job data defaults. */
  buildPickupStop(dataOverrides = {}) {
    const taskJob = { ...DEFAULT_PICKUP_JOB2, data: { ...DEFAULT_PICKUP_JOB2.data, ...dataOverrides } };
    const stopJob = buildConsistentStopJob({ jobs: [taskJob] });
    return this.build({ jobs: [stopJob] });
  }
};
var enrichedStopFactory = new EnrichedStopFactory();

// src/stops-queue/stops-queue.interface.ts
var STOPS_QUEUE_STATUS = {
  ENDED: "ended",
  ONGOING: "ongoing",
  PLANNED: "planned"
};
var ROUTE_SUMMARY_SOURCE = { ETA: "eta", OPTIMIZATION: "optimization" };
var STOP_COMMENT_TARGET_KIND = {
  CUSTOMER_ADDRESS: "customerAddress",
  SITE: "site"
};

// src/test-factories/stops-queue.factory.ts
var StopJobFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      id: "aabbccddeeff001122334455",
      orderId: "bbccddeeff001122334455aa",
      sortIndex: 0,
      taskId: "ccddeeff001122334455aabb",
      type: TASK_JOB_TYPE.DELIVERY
    };
  }
};
var stopJobFactory = new StopJobFactory();
var StopFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      arrivedAt: void 0,
      completedJobAt: void 0,
      eta: void 0,
      failedJobAt: void 0,
      fullAddress: "1 Rue de Paris",
      id: "ddeeff001122334455aabbcc",
      initialEta: void 0,
      jobs: [stopJobFactory.build()],
      routePolyline: void 0,
      timeSlot: void 0,
      userComment: void 0
    };
  }
};
var stopFactory = new StopFactory();
var StopsQueueFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      carrierId: "ccddee112233445566778899",
      driverId: "aabb1122334455667788aabb",
      status: STOPS_QUEUE_STATUS.ONGOING,
      stops: []
    };
  }
};
var stopsQueueFactory = new StopsQueueFactory();

// src/servicebus/optimization.ts
var OPTIMIZATION_OBJECTIVE = { DISTANCE: "distance", TIME: "time" };
var OPTIMIZATION_WAYPOINT_ROLE = { DELIVERY: "delivery", PICKUP: "pickup" };
var OPTIMIZATION_CONSTRAINT = { LINK: "link", TIME_WINDOW: "time-window" };
var OPTIMIZATION_MESSAGE_KIND = { POLL: "poll", REQUEST: "request" };
function isOptimizationPollPayload(payload) {
  return typeof payload === "object" && payload !== null && "kind" in payload && payload.kind === OPTIMIZATION_MESSAGE_KIND.POLL;
}
var OPTIMIZATION_ERROR_CODE = {
  ENGINE_CALL_FAILED: "ENGINE_CALL_FAILED",
  ENGINE_TIMEOUT: "ENGINE_TIMEOUT",
  INVALID_REQUEST: "INVALID_REQUEST",
  NO_CAPABLE_ENGINE: "NO_CAPABLE_ENGINE",
  NO_SOLUTION_FOUND: "NO_SOLUTION_FOUND",
  TOO_MANY_WAYPOINTS: "TOO_MANY_WAYPOINTS"
};

// src/test-factories/optimization-request-payload.factory.ts
var OptimizationRequestPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      objective: OPTIMIZATION_OBJECTIVE.TIME,
      waypoints: [
        {
          coordinates: { latitude: 48.8606, longitude: 2.3376 },
          id: "waypoint-pickup-1",
          link: { linkId: "task-1", role: OPTIMIZATION_WAYPOINT_ROLE.PICKUP },
          serviceTimeInSeconds: 300
        },
        {
          coordinates: { latitude: 48.8738, longitude: 2.295 },
          id: "waypoint-delivery-1",
          link: { linkId: "task-1", role: OPTIMIZATION_WAYPOINT_ROLE.DELIVERY },
          serviceTimeInSeconds: 300
        },
        {
          coordinates: { latitude: 48.8529, longitude: 2.3499 },
          id: "waypoint-standalone",
          serviceTimeInSeconds: 180
        }
      ],
      vehicles: [{ id: "vehicle-1", start: { coordinates: { latitude: 48.8566, longitude: 2.3522 } } }]
    };
  }
};
var optimizationRequestPayloadFactory = new OptimizationRequestPayloadFactory();

// src/test-factories/optimization-success-payload.factory.ts
var OptimizedWaypointFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      estimatedArrivalTime: void 0,
      estimatedDepartureTime: void 0,
      fulfilledConstraints: void 0,
      id: "waypoint-1",
      sequence: 0
    };
  }
};
var optimizedWaypointFactory = new OptimizedWaypointFactory();
var OptimizationRouteFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      departureTime: void 0,
      legs: void 0,
      totalDistanceInMeters: void 0,
      totalTimeInSeconds: void 0,
      vehicleId: "vehicle-1",
      waypoints: [optimizedWaypointFactory.build()]
    };
  }
};
var optimizationRouteFactory = new OptimizationRouteFactory();
var OptimizationSuccessPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      engine: "here",
      routes: [optimizationRouteFactory.build()],
      totalDistanceInMeters: void 0,
      totalTimeInSeconds: void 0,
      unassignedWaypoints: void 0
    };
  }
};
var optimizationSuccessPayloadFactory = new OptimizationSuccessPayloadFactory();

// src/test-factories/task-proof.factory.ts
var DEFAULT_CREATED_BY = {
  firstName: "Kevin",
  lastName: "Martin",
  userId: "aabbccddeeff001122334466"
};
var DEFAULT_IMAGES = [
  { originalUrl: "https://example.com/photo-1.jpg", thumbnailUrl: "https://example.com/photo-1-thumb.jpg" },
  { originalUrl: "https://example.com/photo-2.jpg", thumbnailUrl: "https://example.com/photo-2-thumb.jpg" }
];
function buildBaseProof() {
  return {
    createdAt: /* @__PURE__ */ new Date("2026-05-24T14:18:00.000Z"),
    createdBy: DEFAULT_CREATED_BY,
    id: "aabbccddeeff001122334455"
  };
}
var TaskProofFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...buildBaseProof(),
      comment: "Left at the front door as requested.",
      type: TASK_PROOF_TYPE.COMMENT
    };
  }
  /** Builds a signature proof (single image/URL). */
  buildSignature(overrides = {}) {
    return {
      ...buildBaseProof(),
      signature: "https://example.com/signature.png",
      type: TASK_PROOF_TYPE.SIGNATURE,
      ...overrides
    };
  }
  /** Builds an images proof (multiple photos). */
  buildImages(overrides = {}) {
    return {
      ...buildBaseProof(),
      images: DEFAULT_IMAGES,
      type: TASK_PROOF_TYPE.IMAGES,
      ...overrides
    };
  }
};
var taskProofFactory = new TaskProofFactory();

// src/test-factories/order-type.factory.ts
var OrderTypeFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "standard",
      isSystemic: false,
      label: "Standard"
    };
  }
};
var orderTypeFactory = new OrderTypeFactory();

// src/test-factories/email-template.factory.ts
var EmailTemplateFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      templateId: "d-sendgrid-template-id",
      templateName: "welcome-email"
    };
  }
};
var emailTemplateFactory = new EmailTemplateFactory();

// src/email/index.ts
var EMAIL_STATUS = {
  ERROR: "error",
  PENDING: "pending",
  SENT: "sent",
  SKIPPED: "skipped"
};

// src/test-factories/emitted-email.factory.ts
var EmittedEmailFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      events: [],
      identifiers: { tenantId: "tenant-test" },
      responseTopic: "",
      sgMessageId: "",
      status: EMAIL_STATUS.PENDING,
      templateData: { name: "John" },
      templateName: "welcome-email",
      to: "john@example.com"
    };
  }
};
var emittedEmailFactory = new EmittedEmailFactory();

// src/test-factories/firebase-token.factory.ts
var FirebaseDecodedIdTokenFactory = class extends ObjectFactory {
  getDefaults() {
    const now = Math.floor(Date.now() / 1e3);
    return {
      aud: "pharos-dev-8d6e9",
      auth_time: now,
      email: "test@example.com",
      email_verified: true,
      exp: now + 3600,
      firebase: {
        identities: {},
        sign_in_provider: "microsoft.com"
      },
      iat: now,
      iss: "https://securetoken.google.com/pharos-dev-8d6e9",
      name: "Test User",
      sub: "firebase-uid-001",
      uid: "firebase-uid-001"
    };
  }
};
var firebaseTokenFactory = new FirebaseDecodedIdTokenFactory();

// src/invitation/invitation.interface.ts
var MS_PER_DAY = 864e5;
var INVITATION_EXPIRY_DAYS = 3;
var INVITATION_EXPIRY_DAYS_IN_MS = INVITATION_EXPIRY_DAYS * MS_PER_DAY;
var INVITATION_CHANNEL = {
  EMAIL: "email",
  SMS: "sms",
  WHATSAPP: "whatsapp"
};
var INVITATION_STATUS = {
  ACCEPTED: "accepted",
  CANCELLED: "cancelled",
  CREATED: "created",
  EXPIRED: "expired",
  PENDING: "pending"
};
var INVITATION_STATUS_TRANSITIONS = {
  [INVITATION_STATUS.ACCEPTED]: [],
  [INVITATION_STATUS.CANCELLED]: [INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.CREATED]: [INVITATION_STATUS.CANCELLED, INVITATION_STATUS.EXPIRED, INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.EXPIRED]: [INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.PENDING]: [INVITATION_STATUS.ACCEPTED, INVITATION_STATUS.CANCELLED, INVITATION_STATUS.EXPIRED]
};

// src/test-factories/invitation.factory.ts
var InvitationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      activationLink: "http://localhost:3100/frontoffice/activate?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9",
      channel: INVITATION_CHANNEL.EMAIL,
      email: "test@example.com",
      expiresAt: new Date(Date.now() + INVITATION_EXPIRY_DAYS_IN_MS),
      lastSentAt: null,
      status: INVITATION_STATUS.CREATED,
      tokenNonce: 1,
      userId: "aabbccddeeff001122334455"
    };
  }
};
var invitationFactory = new InvitationFactory();

// src/test-factories/role.factory.ts
var RoleFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      key: "TENANT_ADMIN",
      permissions: ["permissions:read", "roles:read", "roles:write", "users:read", "users:write"]
    };
  }
};
var roleFactory = new RoleFactory();

// src/test-factories/webhook-sent-state.factory.ts
var WebhookSentStateFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      entityKey: "order-id",
      lastSentAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      lastSentState: "planned",
      webhookConfigId: "webhook-config-id"
    };
  }
  /** Build a CreateWebhookSentState body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      entityKey: "order-id",
      lastSentAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      lastSentState: "planned",
      webhookConfigId: "webhook-config-id",
      ...overrides
    };
  }
};
var webhookSentStateFactory = new WebhookSentStateFactory();

// src/test-factories/site.factory.ts
var SiteFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "SITE-01",
      color: "#FF5733",
      name: "Main Shop",
      spaceId: "space-id",
      type: SITE_TYPE.SHOP
    };
  }
  /** Build a CreateSite body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      code: "SITE-01",
      color: "#FF5733",
      name: "Main Shop",
      spaceId: "space-id",
      type: SITE_TYPE.SHOP,
      ...overrides
    };
  }
};
var siteFactory = new SiteFactory();

// src/test-factories/site-summary.factory.ts
var SiteSummaryFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      code: "S1",
      color: "#000",
      id: "site-1",
      name: "Main Warehouse",
      type: SITE_TYPE.WAREHOUSE
    };
  }
};
var siteSummaryFactory = new SiteSummaryFactory();

// src/test-factories/space.factory.ts
var SpaceFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "IDF-01",
      description: "\xCEle-de-France regional space",
      name: "\xCEle-de-France"
    };
  }
  /** Build a CreateSpace body (no base document fields). Code is user-provided. */
  buildCreate(overrides = {}) {
    return {
      code: "IDF-01",
      description: "\xCEle-de-France regional space",
      name: "\xCEle-de-France",
      ...overrides
    };
  }
};
var spaceFactory = new SpaceFactory();

// src/test-factories/tenant-default.factory.ts
var TenantDefaultFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      defaultHandlingUnitTypeId: null
    };
  }
};
var tenantDefaultFactory = new TenantDefaultFactory();

// src/tenant.ts
var SUPPORTED_COUNTRIES = ["FR", "LB"];
var TENANT_STATUS = {
  ACTIVE: "active",
  ARCHIVED: "archived",
  DRAFT: "draft",
  INACTIVE: "inactive",
  INITIALIZATION_FAILED: "initialization_failed",
  INITIALIZING: "initializing"
};
var TENANT_ACTIVITY_TYPE = {
  BULKY_ITEM_DELIVERY: "bulky-item-delivery",
  FOOD_GROCERY_DELIVERY: "food-grocery-delivery",
  OTHER: "other",
  PARCEL_EXPRESS_DELIVERY: "parcel-express-delivery"
};
var TENANT_STATUSES = [
  TENANT_STATUS.ACTIVE,
  TENANT_STATUS.ARCHIVED,
  TENANT_STATUS.INACTIVE
];
var TENANT_SEGMENT = {
  DELIVERY_SERVICE_PARTNER: "delivery-service-partner"
};
var Tenant = {
  collection: "tenants",
  name: "tenant"
};

// src/test-factories/tenant.factory.ts
var TenantFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "tenant-code",
      country: "FR",
      initializedAt: null,
      language: "fr",
      mainContact: {
        email: "contact@example.com",
        firstName: "Jean",
        lastName: "Dupont"
      },
      name: "Test Tenant",
      segment: "delivery-service-partner",
      status: TENANT_STATUS.INACTIVE
    };
  }
};
var tenantFactory = new TenantFactory();

// src/test-factories/tenant-initial-site.factory.ts
var TenantInitialSiteFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      code: "main-warehouse",
      location: {
        address: { city: "Paris", country: "FR", postalCode: "75001", street: "1 rue de Paris" },
        coordinates: { latitude: 48.8566, longitude: 2.3522, score: 1, source: GEOCODING_SOURCE.MANUAL }
      },
      name: "Main Warehouse",
      phone: { dialCode: "+33", number: "612345678" },
      type: SITE_TYPE.WAREHOUSE
    };
  }
};
var tenantInitialSiteFactory = new TenantInitialSiteFactory();

// src/user/user.interface.ts
var USER_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  PENDING: "pending"
};

// src/test-factories/user.factory.ts
var UserFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      canBeReinvited: true,
      email: "test@example.com",
      firstName: "Test",
      lastActive: null,
      lastName: "User",
      phone: null,
      roles: ["TENANT_ADMIN"],
      scopes: [],
      status: USER_STATUS.ACTIVE
    };
  }
};
var userFactory = new UserFactory();

// src/user-preference/user-preference.interface.ts
var USER_PREFERENCE_CATEGORY = {
  ALL: "all",
  BACKOFFICE: "backoffice",
  FRONTOFFICE: "frontoffice",
  KLAREO_GO: "klareo_go"
};

// src/test-factories/user-preference.factory.ts
var UserPreferenceFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      category: USER_PREFERENCE_CATEGORY.FRONTOFFICE,
      key: "orders-table-hidden-columns",
      userId: "aabbccddeeff001122334455",
      value: ["pickupSite", "deliverySite"]
    };
  }
};
var userPreferenceFactory = new UserPreferenceFactory();

// src/test-factories/user-with-resolved-scopes.factory.ts
var UserWithResolvedScopesFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...userFactory.build(),
      resolvedScopes: []
    };
  }
};
var userWithResolvedScopesFactory = new UserWithResolvedScopesFactory();

// src/vehicle/vehicle.interface.ts
var VEHICLE_STATUS = {
  AVAILABLE: "available",
  IN_MAINTENANCE: "in-maintenance"
};

// src/test-factories/vehicle.factory.ts
var VehicleFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      carrierId: "aabbccddeeff001122334455",
      licensePlate: "AB-123-CD",
      siteIds: [],
      spaceId: "bbbbbbbbbbbbbbbbbbbbbbbb",
      status: VEHICLE_STATUS.AVAILABLE,
      vehicleTemplateId: "cccccccccccccccccccccccc"
    };
  }
};
var vehicleFactory = new VehicleFactory();

// src/measurement/measurement.interface.ts
var VOLUME_UNITS = { L: "L", M3: "m3" };
var WEIGHT_UNITS = { KG: "kg", LB: "lb" };

// src/test-factories/vehicle-template.factory.ts
var VehicleTemplateFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      code: "standard-vehicle",
      maxOrderCapacity: 50,
      maxVolumeCapacity: { unit: VOLUME_UNITS.M3, value: 10 },
      maxWeightCapacity: { unit: WEIGHT_UNITS.KG, value: 500 },
      name: "Standard Vehicle",
      profile: VEHICLE_PROFILES.LIGHTWEIGHT_VEHICLE
    };
  }
};
var vehicleTemplateFactory = new VehicleTemplateFactory();

// src/test-factories/webhook-config.factory.ts
var WebhookConfigFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      active: true,
      events: ["on-site-created"],
      identifiers: { shipperCode: "default-shipper" },
      name: "My Webhook",
      url: "https://example.com/webhook"
    };
  }
  /** Build a CreateWebhookConfig body (no base document fields). */
  buildCreate(overrides = {}) {
    return {
      active: true,
      events: ["on-site-created"],
      identifiers: { shipperCode: "default-shipper" },
      name: "My Webhook",
      url: "https://example.com/webhook",
      ...overrides
    };
  }
};
var webhookConfigFactory = new WebhookConfigFactory();

// src/servicebus/template-provisioning.ts
var TEMPLATE_PROVISIONING_TARGET = {
  INCOMING: "incoming",
  OUTGOING: "outgoing"
};
var PROVISIONING_TARGET_STATUS = {
  FAILED: "failed",
  PENDING: "pending",
  SUCCESS: "success"
};

// src/template-configuration/template-configuration.interface.ts
var TEMPLATE_CONFIGURATION_STATUS = {
  ACTIVE: "active",
  ARCHIVED: "archived",
  INACTIVE: "inactive",
  PENDING: "pending"
};
var TEMPLATE_CONFIGURATION_STATUS_TRANSITIONS = {
  [TEMPLATE_CONFIGURATION_STATUS.ACTIVE]: [
    TEMPLATE_CONFIGURATION_STATUS.INACTIVE,
    TEMPLATE_CONFIGURATION_STATUS.ARCHIVED
  ],
  [TEMPLATE_CONFIGURATION_STATUS.ARCHIVED]: [],
  [TEMPLATE_CONFIGURATION_STATUS.INACTIVE]: [
    TEMPLATE_CONFIGURATION_STATUS.ACTIVE,
    TEMPLATE_CONFIGURATION_STATUS.ARCHIVED
  ],
  [TEMPLATE_CONFIGURATION_STATUS.PENDING]: [
    TEMPLATE_CONFIGURATION_STATUS.ACTIVE,
    TEMPLATE_CONFIGURATION_STATUS.ARCHIVED
  ]
};

// src/test-factories/template-configuration.factory.ts
var TemplateConfigurationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      status: TEMPLATE_CONFIGURATION_STATUS.PENDING,
      templateId: "template-id-1",
      values: {}
    };
  }
  /** Returns a config where provisioning has been dispatched (both targets pending). */
  buildProvisioning() {
    return this.build({
      provisioning: {
        incoming: { status: PROVISIONING_TARGET_STATUS.PENDING },
        outgoing: { status: PROVISIONING_TARGET_STATUS.PENDING }
      },
      status: TEMPLATE_CONFIGURATION_STATUS.PENDING
    });
  }
  /** Returns a fully provisioned active config (both targets succeeded). */
  buildActive() {
    return this.build({
      provisioning: {
        [TEMPLATE_PROVISIONING_TARGET.INCOMING]: {
          status: PROVISIONING_TARGET_STATUS.SUCCESS
        },
        [TEMPLATE_PROVISIONING_TARGET.OUTGOING]: {
          status: PROVISIONING_TARGET_STATUS.SUCCESS
        }
      },
      status: TEMPLATE_CONFIGURATION_STATUS.ACTIVE
    });
  }
  /** Returns a config where one or more targets failed (overall status resets to pending for retry). */
  buildFailed() {
    return this.build({
      provisioning: {
        [TEMPLATE_PROVISIONING_TARGET.INCOMING]: {
          error: "Upstream service unavailable",
          status: PROVISIONING_TARGET_STATUS.FAILED
        },
        [TEMPLATE_PROVISIONING_TARGET.OUTGOING]: {
          status: PROVISIONING_TARGET_STATUS.SUCCESS
        }
      },
      status: TEMPLATE_CONFIGURATION_STATUS.PENDING
    });
  }
};
var templateConfigurationFactory = new TemplateConfigurationFactory();

// src/queue-optimization/queue-optimization.interface.ts
var QUEUE_OPTIMIZATION_STATUS = {
  CANCELLED: "cancelled",
  CONFIRMED: "confirmed",
  FAILED: "failed",
  PENDING: "pending",
  READY: "ready"
};
var QUEUE_OPTIMIZATION_ARRIVAL_STATUS = { EARLY: "early", LATE: "late", ON_TIME: "on_time" };
var QUEUE_OPTIMIZATION_ERROR_CODE = {
  ENGINE_FAILED: "ENGINE_FAILED",
  QUEUE_CHANGED: "QUEUE_CHANGED",
  TOO_MANY_WAYPOINTS: "TOO_MANY_WAYPOINTS"
};

// src/test-factories/queue-optimization.factory.ts
var QueueOptimizationJobFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      estimatedArrivalTime: void 0,
      estimatedDepartureTime: void 0,
      jobId: "eeff001122334455aabbccdd",
      sequence: 0,
      taskId: "ff001122334455aabbccddee"
    };
  }
};
var queueOptimizationJobFactory = new QueueOptimizationJobFactory();
var QueueOptimizationStopFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      arrivalStatus: QUEUE_OPTIMIZATION_ARRIVAL_STATUS.ON_TIME,
      estimatedArrivalTime: void 0,
      fullAddress: "1 Rue de Paris",
      jobs: [queueOptimizationJobFactory.build()],
      routePolyline: void 0
    };
  }
};
var queueOptimizationStopFactory = new QueueOptimizationStopFactory();
var QueueOptimizationFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      ...baseDocumentFactory.build(),
      driverId: "aabbccddeeff001122334455",
      expiresAt: /* @__PURE__ */ new Date("2026-01-01T00:30:00.000Z"),
      objective: OPTIMIZATION_OBJECTIVE.TIME,
      requestedAt: /* @__PURE__ */ new Date("2026-01-01T00:00:00.000Z"),
      skippedJobIds: [],
      snapshotHash: "a".repeat(64),
      startPosition: { latitude: 48.8566, longitude: 2.3522 },
      status: QUEUE_OPTIMIZATION_STATUS.PENDING,
      stopsQueueId: "bbccddeeff001122334455aa"
    };
  }
};
var queueOptimizationFactory = new QueueOptimizationFactory();

// src/test-factories/queue-optimization-completed-payload.factory.ts
var QueueOptimizationCompletedPayloadFactory = class extends ObjectFactory {
  getDefaults() {
    return {
      driverId: "aabbccddeeff001122334455",
      runId: "bbccddeeff001122334455aa",
      status: QUEUE_OPTIMIZATION_STATUS.READY
    };
  }
};
var queueOptimizationCompletedPayloadFactory = new QueueOptimizationCompletedPayloadFactory();

// src/test-factories/pagination.factory.ts
function buildPaginatedResponse(items, overrides = {}) {
  return {
    items,
    page: 1,
    pageSize: 20,
    total: items.length,
    totalPages: 1,
    ...overrides
  };
}

// src/global.configuration.ts
var INTERNAL_TENANT_ID = "000000000000000000000000";
var PharosGlobalConfiguration = {
  auth: {
    internalEmailDomains: ["@klareo.com", "@klareo.internal"]
  }
};

// src/carrier/carrier.interface.ts
var CARRIER_TYPE = {
  LOCAL: "LOCAL",
  NATIONAL: "NATIONAL"
};
var SITE_CARRIER_STATUS = {
  ENABLED: "enabled",
  EXPIRED: "expired",
  SCHEDULED: "scheduled"
};
var CARRIER_ENABLEMENT_SCOPE = {
  SITE: "site",
  SPACE: "space"
};
var CARRIER_CASCADE_MODE = {
  ACKNOWLEDGE: "acknowledge",
  CASCADE: "cascade",
  PREVENT: "prevent"
};
var CARRIER_CASCADE_ACTION = {
  ARCHIVE: "archive",
  UNREFERENCE: "unreference"
};
var Carrier = {
  collection: "carriers",
  name: "carrier"
};

// src/handling-unit-type/handling-unit-type.interface.ts
var HandlingUnitType = {
  collection: "handlingunittypes",
  name: "handling-unit-type"
};

// src/import/import.constants.ts
var IMPORT_SUPPORTED_EXTENSIONS = ["csv", "xlsx"];
var MAX_IMPORT_FILE_SIZE_BYTES = 5 * 1024 * 1024;
var IMPORT_ERROR_REPORT_COLUMN = "Error type";
var MAX_REPORTED_LINE_ERRORS = 50;
var IMPORT_GLOBAL_ERROR_CODE = {
  TOO_MANY_LINE_ERRORS: "TooManyLineErrors"
};

// src/tenant-default/tenant-default.interface.ts
var TenantDefault = {
  collection: "tenantdefaults",
  name: "tenant-default"
};

// src/order-type/order-type.interface.ts
var ORDER_TYPE_SYSTEMIC_CODES = {
  CART_DROP_OFF: "cartdropoff",
  ECOMMERCE: "e-commerce"
};
var SYSTEMIC_ORDER_TYPES = [
  { code: ORDER_TYPE_SYSTEMIC_CODES.CART_DROP_OFF, label: "Cart Drop Off" },
  { code: ORDER_TYPE_SYSTEMIC_CODES.ECOMMERCE, label: "E-commerce" }
];
var OrderType = {
  collection: "ordertypes",
  name: "order-type"
};

// src/order/order-update-fields.ts
var ORDER_UPDATE_FIELD_CATEGORY = {
  NON_EDITABLE: "non_editable",
  NON_IMPACTING: "non_impacting",
  PLANNING_IMPACTING: "planning_impacting"
};
var TOP_LEVEL_FIELD_CATEGORY = {
  handlingUnits: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  instruction: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  orderValue: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  requestedCarrierCode: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  requestedTimeWindow: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  tags: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  totalHandlingUnitQuantity: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  totalVolume: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  totalWeight: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING
};
var ADDRESS_SUBFIELD_CATEGORY = {
  additionalInformations: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  city: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  complement: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  coordinates: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  country: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  digicode: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  floor: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  full: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  hasElevator: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  houseNumber: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  postalCode: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING,
  street: ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING
};
var CONTACT_SUBFIELD_CATEGORY = {
  name: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  phone: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING
};
var CUSTOMER_SUBFIELD_CATEGORY = {
  email: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  firstName: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  id: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  lastName: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING,
  phone: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING
};
function categorizeOrderUpdate(patch) {
  const result = {
    [ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE]: [],
    [ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING]: [],
    [ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING]: []
  };
  for (const [key, value] of Object.entries(patch)) {
    if (value === void 0) continue;
    for (const field of resolveTopLevelField(key, value)) {
      result[field.category].push(field.path);
    }
  }
  return result;
}
function resolveTopLevelField(key, value) {
  if (key === "customer") {
    return resolveNestedFields("customer", value, CUSTOMER_SUBFIELD_CATEGORY);
  }
  if (key === "delivery") {
    return resolveDeliveryFields(value);
  }
  if (key === "pickup") {
    return resolvePickupFields(value);
  }
  return [{ category: TOP_LEVEL_FIELD_CATEGORY[key] ?? ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: key }];
}
function resolveDeliveryFields(value) {
  if (!isPlainObject(value)) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: "delivery" }];
  }
  const fields = [];
  for (const [subKey, subValue] of Object.entries(value)) {
    if (subValue === void 0) continue;
    if (subKey === "address") {
      fields.push(...resolveNestedFields("delivery.address", subValue, ADDRESS_SUBFIELD_CATEGORY));
    } else if (subKey === "contact") {
      fields.push(...resolveContactField("delivery.contact", subValue));
    } else if (subKey === "timeSlot") {
      fields.push({
        category: isPlainObject(subValue) ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: "delivery.timeSlot"
      });
    } else if (subKey === "durationTime") {
      fields.push({
        category: typeof subValue === "number" ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: "delivery.durationTime"
      });
    } else {
      fields.push({ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: `delivery.${subKey}` });
    }
  }
  return fields;
}
function resolvePickupFields(value) {
  if (!isPlainObject(value)) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: "pickup" }];
  }
  const fields = [];
  for (const [subKey, subValue] of Object.entries(value)) {
    if (subValue === void 0) continue;
    if (subKey === "contact") {
      fields.push(...resolveContactField("pickup.contact", subValue));
    } else if (subKey === "timeSlot") {
      fields.push({
        category: isPlainObject(subValue) ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: "pickup.timeSlot"
      });
    } else if (subKey === "durationTime") {
      fields.push({
        category: typeof subValue === "number" ? ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING : ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
        path: "pickup.durationTime"
      });
    } else {
      fields.push({ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: `pickup.${subKey}` });
    }
  }
  return fields;
}
function resolveContactField(prefix, value) {
  if (value === null) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_IMPACTING, path: prefix }];
  }
  return resolveNestedFields(prefix, value, CONTACT_SUBFIELD_CATEGORY);
}
function resolveNestedFields(prefix, value, categoryBySubfield) {
  if (!isPlainObject(value)) {
    return [{ category: ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE, path: prefix }];
  }
  return Object.entries(value).filter(([, subValue]) => subValue !== void 0).map(([subKey]) => ({
    category: categoryBySubfield[subKey] ?? ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE,
    path: `${prefix}.${subKey}`
  }));
}
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// src/order/order-update-rejection.interface.ts
var ORDER_UPDATE_REJECTION_REASON = {
  INELIGIBLE_VALUE: "ineligible_value",
  NON_EDITABLE_FIELD: "non_editable_field",
  ORDER_IN_EXECUTION: "order_in_execution",
  ORDER_TERMINAL: "order_terminal"
};
var ORDER_UPDATE_REJECTION_SOURCE = {
  API: "api",
  IMPORT: "import",
  INGEST: "ingest"
};

// src/routing/routing.interface.ts
var ROUTING_ERROR_CODE = {
  NO_ROUTE: "NO_ROUTE",
  ROUTING_FAILED: "ROUTING_FAILED"
};
var ROUTING_PURPOSE = { QUEUE_OPTIMIZATION: "queue-optimization" };

// src/driver-position/driver-position.interface.ts
var DRIVER_POSITION_SOURCE_KIND = {
  ARRIVAL_DECLARED: "arrival_declared",
  JOB_COMPLETED: "job_completed",
  JOB_FAILED: "job_failed",
  MONITORING: "monitoring"
};

// src/site/site-preset-colors.ts
var SITE_PRESET_COLORS = [
  { hex: "#EA4D3D", name: "RED" },
  { hex: "#EF8C00", name: "ORANGE" },
  { hex: "#F6CE46", name: "YELLOW" },
  { hex: "#64C466", name: "GREEN" },
  { hex: "#6CE4CF", name: "CYAN" },
  { hex: "#5AC4F6", name: "LIGHT_BLUE" },
  { hex: "#295FF4", name: "BLUE" },
  { hex: "#782CF6", name: "PURPLE" },
  { hex: "#EB539F", name: "MAGENTA" }
];

// src/permissions/built-in.ts
var TASKS_LIST_ALL_PERMISSION = "tasks:list-all";
var TASKS_EXPORT_HISTORY_PERMISSION = "tasks:export-history";
var BUILT_IN_ROLE_KEY = {
  ACCOUNT_MANAGER: "ACCOUNT_MANAGER",
  CARRIER: "CARRIER",
  DRIVER: "DRIVER",
  SITE_MANAGER: "SITE_MANAGER",
  SITE_OPERATOR: "SITE_OPERATOR",
  SPACE_ADMIN: "SPACE_ADMIN",
  TENANT_ADMIN: "TENANT_ADMIN"
};
var builtInRoles = {
  [BUILT_IN_ROLE_KEY.ACCOUNT_MANAGER]: {
    permissions: [
      "backoffice:access",
      "billing:manage",
      "billing:read",
      "invitations:manage",
      "invitations:read",
      "permissions:read",
      "roles:manage",
      "roles:read",
      "spaces:list",
      "spaces:manage",
      "tenant-defaults:read",
      "tenants:initialize",
      "tenants:manage",
      "tenants:read",
      "tracking:read",
      "users:list",
      "users:manage"
    ]
  },
  [BUILT_IN_ROLE_KEY.CARRIER]: { permissions: ["frontoffice:access", "tasks:list"] },
  [BUILT_IN_ROLE_KEY.DRIVER]: {
    functions: { isDriver: true },
    permissions: [
      "driver-positions:write",
      "drivers:list",
      "handling-unit-types:list",
      "mobile:access",
      "orders:read",
      "shippers:list",
      "sites:list",
      "tasks:execute",
      "tasks:export-history",
      "tasks:list",
      "tasks:release",
      "tasks:reorder",
      "tasks:self-assign"
    ]
  },
  [BUILT_IN_ROLE_KEY.SITE_MANAGER]: {
    permissions: [
      "carriers-sites:list",
      "carriers:access-read-only",
      "carriers:list",
      "carriers:manage",
      "customers:list",
      "customers:manage",
      "drivers:list",
      "drivers:manage",
      "endpoints:manage",
      "endpoints:read",
      "frontoffice:access",
      "handling-unit-types:list",
      "kpis:read",
      "order-types:list",
      "orders:cancel",
      "orders:list",
      "orders:manage",
      "orders:read",
      "orders:write",
      "permissions:read",
      "roles:read",
      "shippers:list",
      "sites:access-read-only",
      "sites:list",
      "sites:manage",
      "sites:update",
      "tasks:assign",
      "tasks:list",
      "tasks:list-all",
      "tenant-defaults:read",
      "tracking:read",
      "users:access-read-only",
      "users:list",
      "users:manage",
      "vehicle-templates:list",
      "vehicles:list"
    ]
  },
  [BUILT_IN_ROLE_KEY.SITE_OPERATOR]: {
    permissions: [
      "carriers-sites:list",
      "carriers:list",
      "customers:list",
      "drivers:list",
      "frontoffice:access",
      "handling-unit-types:list",
      "kpis:read",
      "order-types:list",
      "orders:cancel",
      "orders:list",
      "orders:manage",
      "orders:read",
      "orders:write",
      "permissions:read",
      "roles:read",
      "shippers:list",
      "sites:access-read-only",
      "sites:list",
      "tasks:assign",
      "tasks:list",
      "tasks:list-all",
      "tenant-defaults:read",
      "tracking:read",
      "users:list",
      "vehicle-templates:list",
      "vehicles:list"
    ]
  },
  [BUILT_IN_ROLE_KEY.SPACE_ADMIN]: {
    permissions: [
      "carriers-sites:list",
      "carriers-sites:manage",
      "carriers:access-read-only",
      "carriers:list",
      "carriers:manage",
      "customers:list",
      "customers:manage",
      "drivers:list",
      "endpoints:manage",
      "endpoints:read",
      "frontoffice:access",
      "handling-unit-types:list",
      "kpis:read",
      "order-types:list",
      "order-types:manage",
      "orders:cancel",
      "orders:list",
      "orders:manage",
      "orders:read",
      "orders:write",
      "permissions:read",
      "roles:manage",
      "roles:read",
      "shippers:list",
      "sites:access-read-only",
      "sites:create",
      "sites:list",
      "sites:manage",
      "sites:update",
      "spaces:access-read-only",
      "spaces:list",
      "spaces:manage",
      "tasks:assign",
      "tasks:list",
      "tasks:list-all",
      "tenant-defaults:read",
      "users:access-read-only",
      "users:list",
      "users:manage",
      "vehicle-templates:list",
      "vehicle-templates:manage",
      "vehicles:list",
      "vehicles:manage"
    ]
  },
  [BUILT_IN_ROLE_KEY.TENANT_ADMIN]: {
    permissions: [
      "billing:read-own",
      "carriers-sites:list",
      "carriers-sites:manage",
      "carriers:access-read-only",
      "carriers:list",
      "carriers:manage",
      "customers:list",
      "customers:manage",
      "drivers:list",
      "drivers:manage",
      "endpoints:manage",
      "endpoints:read",
      "frontoffice:access",
      "handling-unit-types:list",
      "handling-unit-types:manage",
      "invitations:manage",
      "invitations:read",
      "kpis:read",
      "order-types:list",
      "order-types:manage",
      "orders:cancel",
      "orders:list",
      "orders:manage",
      "orders:read",
      "orders:write",
      "permissions:read",
      "roles:manage",
      "roles:read",
      "servicekeys:manage",
      "servicekeys:read",
      "shippers:list",
      "shippers:manage",
      "sites:access-read-only",
      "sites:create",
      "sites:list",
      "sites:manage",
      "sites:update",
      "spaces:access-read-only",
      "spaces:list",
      "spaces:manage",
      "tasks:assign",
      "tasks:list",
      "tasks:list-all",
      "tenant-defaults:read",
      "tenant-defaults:write",
      "tenants:access-read-only",
      "tenants:initialize",
      "tenants:manage",
      "tenants:read",
      "tracking:read",
      "users:access-read-only",
      "users:list",
      "users:manage",
      "vehicle-templates:list",
      "vehicle-templates:manage",
      "vehicles:list",
      "vehicles:manage",
      "webhooks:manage",
      "webhooks:read"
    ]
  }
};

// src/permissions/role.interface.ts
var ROLE_SCOPE = {
  CARRIER: "carrier",
  SITE: "site",
  SPACE: "space",
  TENANT: "tenant"
};
var ROLE_REQUIRED_SCOPE = {
  ACCOUNT_MANAGER: null,
  CARRIER: ROLE_SCOPE.CARRIER,
  // A driver's carrier association lives in driverAttributes.carrierId, not in a scope entry.
  DRIVER: null,
  SITE_MANAGER: ROLE_SCOPE.SITE,
  SITE_OPERATOR: ROLE_SCOPE.SITE,
  SPACE_ADMIN: ROLE_SCOPE.SPACE,
  TENANT_ADMIN: ROLE_SCOPE.TENANT
};

// src/permissions/pharos-scope-access-filter.interface.ts
var DEFAULT_PHAROS_SCOPE_FIELD_MAPPING = {
  carrier: "carrierId",
  site: "siteId",
  space: "spaceId"
};

// src/realtime/index.ts
var REALTIME_USER_EVENTS = {
  ETA_COMPUTED: "eta-computed",
  IMPORT_COMPLETED: "import-completed",
  QUEUE_OPTIMIZATION_COMPLETED: "queue-optimization-completed"
};

// src/servicebus/geocoding.ts
var GEOCODING_ERROR_CODE = {
  GEOCODING_FAILED: "GEOCODING_FAILED",
  NO_RESULTS: "NO_RESULTS"
};

// src/service-bus-topics.ts
var SERVICE_BUS_CRUD_RESOURCES = {
  ENDPOINT_CONFIG: "endpoint-config",
  ORDER: "order",
  WEBHOOK_CONFIG: "webhook-config"
};
var SERVICE_BUS_CRUD_ACTIONS = { CREATE: "create", DELETE: "delete", UPDATE: "update" };
function getCrudRequestTopicName(resource, action) {
  return `${resource}-${action}-request`;
}
var SERVICE_BUS_TOPICS = {
  // ─── Billing topics ─────────────────────────────────────────────────
  BILL_CALCULATION_REQUESTED: "bill-calculation-requested",
  CALL_REQUEST: "on-call-request",
  // ─── Carrier sync topics ────────────────────────────────────────────
  CARRIER_CREATED: "on-carrier-created",
  CARRIER_DELETED: "on-carrier-deleted",
  CARRIER_UPDATED: "on-carrier-updated",
  EMAIL_SEND_REQUESTED: "on-email-send-requested",
  EMAIL_SEND_RESPONSE: "on-email-send-response",
  EMAIL_STATUS_UPDATED: "on-email-status-updated",
  // ─── Endpoint config provisioning topics ────────────────────────────
  ENDPOINT_CONFIG_CREATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.ENDPOINT_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.CREATE
  ),
  ENDPOINT_CONFIG_DELETE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.ENDPOINT_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.DELETE
  ),
  ENDPOINT_CONFIG_UPDATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.ENDPOINT_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.UPDATE
  ),
  // ─── ETA topics ──────────────────────────────────────────────────────
  ETA_COMPUTED: "on-eta-computed",
  GEOCODING_REQUEST: "on-geocoding-request",
  GEOCODING_RESPONSE: "on-geocoding-response",
  // ─── HandlingUnitType sync topics ───────────────────────────────────
  HANDLING_UNIT_TYPE_CREATED: "on-handling-unit-type-created",
  HANDLING_UNIT_TYPE_DELETED: "on-handling-unit-type-deleted",
  HANDLING_UNIT_TYPE_UPDATED: "on-handling-unit-type-updated",
  // ─── Import topics ───────────────────────────────────────────────────
  IMPORT_COMPLETED: "on-import-completed",
  // ─── KPI topics ──────────────────────────────────────────────────────
  KPI_COMPUTED: "on-kpi-computed",
  KPI_COMPUTE_REQUEST: "kpi-compute-request",
  KPI_REFRESH_TICK: "kpi-refresh-tick",
  // ─── Order topics ───────────────────────────────────────────────────
  ORDER_CREATED: "on-order-created",
  // ─── Order import provisioning topics ───────────────────────────────
  ORDER_CREATE_REQUEST: getCrudRequestTopicName(SERVICE_BUS_CRUD_RESOURCES.ORDER, SERVICE_BUS_CRUD_ACTIONS.CREATE),
  ORDER_DELETED: "on-order-deleted",
  ORDER_INGEST_REFUSED: "on-order-ingest-refused",
  ORDER_UPDATE_REQUEST: getCrudRequestTopicName(SERVICE_BUS_CRUD_RESOURCES.ORDER, SERVICE_BUS_CRUD_ACTIONS.UPDATE),
  ORDER_UPDATED: "on-order-updated",
  // ─── OrderType sync topics ──────────────────────────────────────────
  ORDER_TYPE_CREATED: "on-order-type-created",
  ORDER_TYPE_DELETED: "on-order-type-deleted",
  ORDER_TYPE_UPDATED: "on-order-type-updated",
  // ─── Queue optimization topics ───────────────────────────────────────
  QUEUE_OPTIMIZATION_COMPLETED: "on-queue-optimization-completed",
  // ─── Routing topics ──────────────────────────────────────────────────
  ROUTING_REQUEST: "on-routing-request",
  ROUTING_RESPONSE: "on-routing-response",
  // ─── Optimization topics ─────────────────────────────────────────────
  OPTIMIZATION_REQUEST: "on-optimization-request",
  OPTIMIZATION_RESPONSE: "on-optimization-response",
  SITE_CREATED: "on-site-created",
  // ─── Site sync topics ───────────────────────────────────────────────
  SITE_DELETED: "on-site-deleted",
  SITE_UPDATED: "on-site-updated",
  // ─── Task sync topics ───────────────────────────────────────────────
  TASK_CREATED: "on-task-created",
  TASK_DELETED: "on-task-deleted",
  TASK_UPDATED: "on-task-updated",
  TEMPLATE_PROVISIONING_RESULT: "on-template-provisioning-result",
  // ─── Tenant sync topics ─────────────────────────────────────────────
  TENANT_CREATED: "on-tenant-created",
  TENANT_DELETED: "on-tenant-deleted",
  TENANT_UPDATED: "on-tenant-updated",
  // ─── Tenant default sync topics ─────────────────────────────────────────────
  TENANT_DEFAULT_CREATED: "on-tenant-default-created",
  TENANT_DEFAULT_DELETED: "on-tenant-default-deleted",
  TENANT_DEFAULT_UPDATED: "on-tenant-default-updated",
  // ─── Site update command topic ──────────────────────────────────────
  UPDATE_SITE: "update-site",
  // ─── Webhook config provisioning topics ─────────────────────────────
  WEBHOOK_CONFIG_CREATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.WEBHOOK_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.CREATE
  ),
  WEBHOOK_CONFIG_DELETE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.WEBHOOK_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.DELETE
  ),
  WEBHOOK_CONFIG_UPDATE_REQUEST: getCrudRequestTopicName(
    SERVICE_BUS_CRUD_RESOURCES.WEBHOOK_CONFIG,
    SERVICE_BUS_CRUD_ACTIONS.UPDATE
  )
};

// src/push/push-device-token.interface.ts
var PUSH_PLATFORM = {
  ANDROID: "android",
  IOS: "ios"
};

// src/push/push-notification.interface.ts
var PUSH_EVENT_TYPE = {
  TASK_ASSIGNED: "task-assigned",
  TASK_CANCELLED: "task-cancelled",
  TASK_UNASSIGNED: "task-unassigned",
  TASK_UPDATED: "task-updated"
};
var VISIBLE_PUSH_EVENT_TYPES = [
  PUSH_EVENT_TYPE.TASK_ASSIGNED,
  PUSH_EVENT_TYPE.TASK_UNASSIGNED,
  PUSH_EVENT_TYPE.TASK_CANCELLED
];

// src/template/template.interface.ts
var TEMPLATE_TYPE = {
  CARRIER: "carrier",
  SHIPPER: "shipper"
};

// src/authentication/authentication-config.interface.ts
var WEBHOOK_AUTH_TYPE = {
  APIKEY: "apikey",
  HTTP_BASIC: "http-basic",
  JWT: "jwt",
  SIGNATURE: "signature"
};

// src/klareo-headers.ts
var KLAREO_CORRELATION_ID_HEADER = "x-klareo-correlation-id";
var KLAREO_ENDPOINT_ID_HEADER = "x-klareo-endpoint-id";
var KLAREO_CLIENT = {
  MOBILE_DRIVER: "mobile-driver"
};

// src/sync/sync-operation.ts
var SYNC_OPERATION = {
  CREATED: "created",
  DELETED: "deleted",
  UPDATED: "updated"
};

// src/service-key/service-key.interface.ts
var SERVICE_KEY_TYPE = {
  oauth2: "oauth2",
  static: "static"
};

// src/service-token.ts
var SERVICE_TOKEN_TYPE = {
  crossService: "cross-service",
  serviceAccount: "service-account"
};

// src/webview.ts
var WEBVIEW_MESSAGE_TYPE = {
  CLOSE_WEBVIEW: "closeWebview"
};

// src/kpi/order-kpi-status.ts
var ORDER_KPI_STATUS_GROUPS = {
  anomaly: [ORDER_STATUS.ANOMALY],
  completed: [ORDER_STATUS.COMPLETED],
  failed: [ORDER_STATUS.FAILED],
  inprogress: [
    ORDER_STATUS.CARRIER_ASSIGNED,
    ORDER_STATUS.CARRIER_ACCEPTED,
    ORDER_STATUS.DRIVER_ASSIGNED,
    ORDER_STATUS.DRIVER_ACCEPTED,
    ORDER_STATUS.DRIVING_TO_PICKUP,
    ORDER_STATUS.ARRIVED_AT_PICKUP,
    ORDER_STATUS.WAITING_FOR_PICKUP,
    ORDER_STATUS.PICKUP_COMPLETED,
    ORDER_STATUS.IN_TRANSIT,
    ORDER_STATUS.DRIVING_TO_DELIVERY,
    ORDER_STATUS.ARRIVED_AT_DELIVERY
  ],
  unassigned: [ORDER_STATUS.DRAFT, ORDER_STATUS.NEW, ORDER_STATUS.PENDING, ORDER_STATUS.CARRIER_REJECTED]
};
var ORDER_KPI_EXCLUDED_STATUSES = [ORDER_STATUS.CANCELLED];
var ORDER_KPI_COUNTED_STATUSES = [
  ...ORDER_KPI_STATUS_GROUPS.anomaly,
  ...ORDER_KPI_STATUS_GROUPS.unassigned,
  ...ORDER_KPI_STATUS_GROUPS.inprogress,
  ...ORDER_KPI_STATUS_GROUPS.completed,
  ...ORDER_KPI_STATUS_GROUPS.failed
];
var ORDER_KPI_METRIC_KEYS = [
  "anomaly",
  "completed",
  "completedLate",
  "completedOnTime",
  "failed",
  "inprogress",
  "total",
  "unassigned"
];

// src/kpi/task-kpi-status.ts
var TASK_KPI_STATUS_GROUPS = {
  completed: [TASK_STATUS.COMPLETED],
  failed: [TASK_STATUS.FAILED],
  inprogress: [
    TASK_STATUS.DRIVING_TO_PICKUP,
    TASK_STATUS.ARRIVED_AT_PICKUP,
    TASK_STATUS.WAITING_FOR_PICKUP,
    TASK_STATUS.PICKUP_COMPLETED,
    TASK_STATUS.IN_TRANSIT,
    TASK_STATUS.DRIVING_TO_DELIVERY,
    TASK_STATUS.ARRIVED_AT_DELIVERY
  ]
};
var TASK_KPI_EXCLUDED_STATUSES = [TASK_STATUS.CANCELLED];
var TASK_KPI_ASSIGNED_ONLY_STATUSES = [
  TASK_STATUS.NEW,
  TASK_STATUS.CARRIER_ASSIGNED,
  TASK_STATUS.CARRIER_ACCEPTED,
  TASK_STATUS.CARRIER_REJECTED,
  TASK_STATUS.DRIVER_ASSIGNED,
  TASK_STATUS.DRIVER_ACCEPTED
];
var TASK_KPI_COUNTED_STATUSES = [
  ...TASK_KPI_ASSIGNED_ONLY_STATUSES,
  ...TASK_KPI_STATUS_GROUPS.inprogress,
  ...TASK_KPI_STATUS_GROUPS.completed,
  ...TASK_KPI_STATUS_GROUPS.failed
];
export {
  ADDRESS_IDENTITY_KEYS,
  BARCODE_TYPES,
  BILLING_CONSTRAINTS,
  BILL_LINE_KEYS,
  BUILT_IN_ROLE_KEY,
  BaseDocumentFactory,
  BillFactory,
  BillingCountedTaskFactory,
  BillingMetricsFactory,
  BillingPlanFactory,
  BillingSettingsFactory,
  CALL_STATUS,
  CARRIER_CASCADE_ACTION,
  CARRIER_CASCADE_MODE,
  CARRIER_ENABLEMENT_SCOPE,
  CARRIER_TYPE,
  CONTROLLED_UNIT_SOURCE,
  CURRENCIES,
  CUSTOMER_STATUS,
  CUSTOMER_TYPE,
  CallRequestFactory,
  Carrier,
  CarrierFactory,
  CarrierSummaryFactory,
  CustomerAddressFactory,
  CustomerFactory,
  DEFAULT_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
  DEFAULT_DIAL_CODE,
  DEFAULT_DURATION_TIME_MINUTES,
  DEFAULT_FREE_TRIAL_DURATION_IN_DAYS,
  DEFAULT_PHAROS_SCOPE_FIELD_MAPPING,
  DRIVER_POSITION_SOURCE_KIND,
  DeliveryJobFactory,
  EMAIL_STATUS,
  ETA_ANCHOR_KIND,
  ETA_FRESHNESS,
  EmailTemplateFactory,
  EmittedEmailFactory,
  EndpointConfigFactory,
  EnrichedStopFactory,
  EtaComputedPayloadFactory,
  EtaFactory,
  FirebaseDecodedIdTokenFactory,
  GEOCODING_ERROR_CODE,
  GEOCODING_SOURCE,
  HANDLING_UNIT_CONTROL_STATUS,
  HANDLING_UNIT_TEMPERATURES,
  HISTORY_CREATED_EVENT_CODE,
  HISTORY_VALUE_KIND,
  HandlingUnitFactory,
  HandlingUnitType,
  HandlingUnitTypeFactory,
  IMPORT_ERROR_REPORT_COLUMN,
  IMPORT_GLOBAL_ERROR_CODE,
  IMPORT_KIND,
  IMPORT_LINE_ERROR_CODE,
  IMPORT_STATE,
  IMPORT_SUPPORTED_EXTENSIONS,
  INTERNAL_TENANT_ID,
  INVITATION_CHANNEL,
  INVITATION_EXPIRY_DAYS,
  INVITATION_EXPIRY_DAYS_IN_MS,
  INVITATION_STATUS,
  INVITATION_STATUS_TRANSITIONS,
  ImportCompletedPayloadFactory,
  ImportFactory,
  IncomingEventFactory,
  InvitationFactory,
  ItemFactory,
  JWT_KIND,
  KLAREO_CLIENT,
  KLAREO_CORRELATION_ID_HEADER,
  KLAREO_ENDPOINT_ID_HEADER,
  KPI_ACTIVE_HEARTBEAT_MS,
  KPI_COMPUTE_TRIGGER,
  KPI_GRANULARITY,
  KPI_KEYS,
  KpiComputeRequestPayloadFactory,
  KpiComputedPayloadFactory,
  KpiFactory,
  KpiRefreshTickPayloadFactory,
  MAX_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
  MAX_IMPORT_FILE_SIZE_BYTES,
  MAX_PROOF_BLOB_PATH_LENGTH,
  MAX_PROOF_UPLOAD_URLS_PER_CALL,
  MAX_REFRESH_SESSION_MS,
  MAX_REPORTED_LINE_ERRORS,
  MAX_STOP_PROOF_PHOTOS,
  MIN_DELIVERY_TIMESLOT_DURATION_IN_HOURS,
  MS_PER_DAY,
  OPTIMIZATION_CONSTRAINT,
  OPTIMIZATION_ERROR_CODE,
  OPTIMIZATION_MESSAGE_KIND,
  OPTIMIZATION_OBJECTIVE,
  OPTIMIZATION_WAYPOINT_ROLE,
  ORDER_HISTORY_EVENT_CODE,
  ORDER_INSTRUCTION_MAX_LENGTH,
  ORDER_KPI_COUNTED_STATUSES,
  ORDER_KPI_EXCLUDED_STATUSES,
  ORDER_KPI_METRIC_KEYS,
  ORDER_KPI_STATUS_GROUPS,
  ORDER_SOURCE_TYPE,
  ORDER_STATUS,
  ORDER_STATUS_BUCKET,
  ORDER_STATUS_REASON,
  ORDER_TYPE_SYSTEMIC_CODES,
  ORDER_UPDATE_FIELD_CATEGORY,
  ORDER_UPDATE_REJECTION_REASON,
  ORDER_UPDATE_REJECTION_SOURCE,
  ObjectFactory,
  OptimizationRequestPayloadFactory,
  OptimizationRouteFactory,
  OptimizationSuccessPayloadFactory,
  OptimizedWaypointFactory,
  Order,
  OrderFactory,
  OrderType,
  OrderTypeFactory,
  PASSWORD_LOWERCASE_REGEX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_NUMBER_REGEX,
  PASSWORD_REGEX,
  PASSWORD_SYMBOL_REGEX,
  PASSWORD_UPPERCASE_REGEX,
  PHONE_NATIONAL_NUMBER_REGEX,
  POSTPONABLE_STATUSES_BY_JOB_TYPE,
  PROOF_BLOB_PATH_PREFIX,
  PROOF_BLOB_PATH_REGEX,
  PROOF_UPLOAD_EXTENSION,
  PROOF_UPLOAD_MIME_TYPE,
  PROVISIONING_TARGET_STATUS,
  PUSH_EVENT_TYPE,
  PUSH_PLATFORM,
  PharosGlobalConfiguration,
  PickupJobFactory,
  QUEUE_OPTIMIZATION_ARRIVAL_STATUS,
  QUEUE_OPTIMIZATION_ERROR_CODE,
  QUEUE_OPTIMIZATION_STATUS,
  QUEUE_PINNED_STATUSES_BY_JOB_TYPE,
  QueueOptimizationCompletedPayloadFactory,
  QueueOptimizationFactory,
  QueueOptimizationJobFactory,
  QueueOptimizationStopFactory,
  REALTIME_USER_EVENTS,
  ROLE_REQUIRED_SCOPE,
  ROLE_SCOPE,
  ROUTE_SUMMARY_SOURCE,
  ROUTING_ERROR_CODE,
  ROUTING_PURPOSE,
  RTL_LANGUAGES,
  RoleFactory,
  SERVICE_BUS_CRUD_ACTIONS,
  SERVICE_BUS_CRUD_RESOURCES,
  SERVICE_BUS_TOPICS,
  SERVICE_KEY_TYPE,
  SERVICE_TOKEN_TYPE,
  SITE_CARRIER_STATUS,
  SITE_PRESET_COLORS,
  SITE_TYPE,
  STOPS_QUEUE_STATUS,
  STOP_COMMENT_TARGET_KIND,
  SUPPORTED_COUNTRIES,
  SUPPORTED_LANGUAGES,
  SYNC_OPERATION,
  SYSTEMIC_ORDER_TYPES,
  Site,
  SiteFactory,
  SiteSummaryFactory,
  SpaceFactory,
  StopFactory,
  StopJobFactory,
  StopsQueueFactory,
  TASKS_EXPORT_HISTORY_PERMISSION,
  TASKS_LIST_ALL_PERMISSION,
  TASK_ASSIGNMENT_EDITABLE_STATUSES,
  TASK_FAILURE_REASON,
  TASK_FAILURE_REASON_JOB_TYPES,
  TASK_HISTORY_EVENT_CODE,
  TASK_HISTORY_PAGE_SIZE,
  TASK_JOB_STATUS,
  TASK_JOB_TYPE,
  TASK_KPI_COUNTED_STATUSES,
  TASK_KPI_EXCLUDED_STATUSES,
  TASK_KPI_STATUS_GROUPS,
  TASK_PROOF_TYPE,
  TASK_RELATIONSHIP,
  TASK_STATUS,
  TASK_STATUS_REASON,
  TASK_TERMINAL_STATUSES,
  TASK_TYPE,
  TEMPLATE_CONFIGURATION_STATUS,
  TEMPLATE_CONFIGURATION_STATUS_TRANSITIONS,
  TEMPLATE_PROVISIONING_TARGET,
  TEMPLATE_TYPE,
  TENANT_ACTIVITY_TYPE,
  TENANT_BILLING_CONFIGURATION_STATUS,
  TENANT_SEGMENT,
  TENANT_STATUS,
  TENANT_STATUSES,
  TERMINAL_ORDER_STATUSES,
  TRANSPORT_TYPE,
  Task,
  TaskFactory,
  TaskProofFactory,
  TemplateConfigurationFactory,
  Tenant,
  TenantBillingConfigurationFactory,
  TenantDefault,
  TenantDefaultFactory,
  TenantFactory,
  TenantInitialSiteFactory,
  USER_PREFERENCE_CATEGORY,
  USER_STATUS,
  UserFactory,
  UserPreferenceFactory,
  UserWithResolvedScopesFactory,
  VEHICLE_PROFILES,
  VEHICLE_STATUS,
  VISIBLE_PUSH_EVENT_TYPES,
  VOLUME_UNITS,
  VehicleFactory,
  VehicleTemplateFactory,
  WEBHOOK_AUTH_TYPE,
  WEBVIEW_MESSAGE_TYPE,
  WEIGHT_UNITS,
  WebhookConfigFactory,
  WebhookSentStateFactory,
  baseDocumentFactory,
  billFactory,
  billingCountedTaskFactory,
  billingMetricsFactory,
  billingPlanFactory,
  billingSettingsFactory,
  buildPaginatedResponse,
  callRequestFactory,
  carrierFactory,
  carrierSummaryFactory,
  categorizeOrderUpdate,
  customerAddressFactory,
  customerFactory,
  deliveryJobFactory,
  emailTemplateFactory,
  emittedEmailFactory,
  endpointConfigFactory,
  enrichedStopFactory,
  etaComputedPayloadFactory,
  etaFactory,
  firebaseTokenFactory,
  getCrudRequestTopicName,
  handlingUnitFactory,
  handlingUnitTypeFactory,
  importCompletedPayloadFactory,
  importFactory,
  incomingEventFactory,
  invitationFactory,
  isDriverTaskKpiRows,
  isHandlingUnitTemperature,
  isKpiKey,
  isOptimizationPollPayload,
  isOrderCancellable,
  isOrderStatus,
  isQueueJobPinned,
  itemFactory,
  kpiComputeRequestPayloadFactory,
  kpiComputedPayloadFactory,
  kpiFactory,
  kpiRefreshTickPayloadFactory,
  optimizationRequestPayloadFactory,
  optimizationRouteFactory,
  optimizationSuccessPayloadFactory,
  optimizedWaypointFactory,
  orderFactory,
  orderTypeFactory,
  pickupJobFactory,
  queueOptimizationCompletedPayloadFactory,
  queueOptimizationFactory,
  queueOptimizationJobFactory,
  queueOptimizationStopFactory,
  roleFactory,
  siteFactory,
  siteSummaryFactory,
  spaceFactory,
  stopFactory,
  stopJobFactory,
  stopsQueueFactory,
  taskFactory,
  taskProofFactory,
  templateConfigurationFactory,
  tenantBillingConfigurationFactory,
  tenantDefaultFactory,
  tenantFactory,
  tenantInitialSiteFactory,
  userFactory,
  userPreferenceFactory,
  userWithResolvedScopesFactory,
  vehicleFactory,
  vehicleTemplateFactory,
  webhookConfigFactory,
  webhookSentStateFactory,
  withProofs,
  withUnitsControl
};
//# sourceMappingURL=index.mjs.map